import { observer } from 'mobx-react-lite'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

// evertel
import { DocumentServiceProvider } from '@evertel/service'
import { Document, WidgetsProvider } from '@evertel/schema-parser'
import { useService } from '@evertel/di'
import { Text, Badge, Card, CardHeader, CardBody, CardFooter, CardHeaderActions, useUI, Button, Icon } from '@evertel/web/ui'
import { DocumentACLsController, DocumentViewController, EverdocMediaUplaodError } from '@evertel/document'
import { ReadWidgets } from './schema-widgets'
import { APIDataDocumentSchema } from '@evertel/types'
import { SessionService } from '@evertel/session/SessionService'
import { DepartmentsAccessController } from '@evertel/departments-access'
import { getSpecificDepartmentsAccess, getRole } from '@evertel/utils'
import { DocumentFooter, DocumentIcon, DocumentMenu, DocumentModalContext, DocumentPlaceholder } from './elements'
import { exportDocumentAsCSV } from '../utils'
import { DocumentOwnerDisplay } from './elements/DocumentOwnerDisplay'
import { DocumentShareModal } from './acl/DocumentShareModal'
import { DocumentVersionHistoryModal } from './version-history/DocumentVersionHistoryModal'


interface DocumentFullViewProps {
    documentId: number
    onEdit: (documentId: number) => void
    onDismiss?: () => void
    /** The share modal is nested inside another modal (see DocumentModal) */
    nested?: boolean
}

const DocumentFullView: React.FC<DocumentFullViewProps> = observer(({
    documentId,
    onEdit,
    onDismiss,
    nested
}) => {

    const { addToast, showAppLoader, isConfirmed } = useUI()
    const navigate = useNavigate()

    const documentViewController = useService(DocumentViewController, [])
    const documentACLController = useService(DocumentACLsController, [])
    const sessionService = useService(SessionService, [])
    const departmentsAccessController = useService(DepartmentsAccessController, [])

    const [isLoading, setIsLoading] = useState(false)
    const [isShareOpen, setIsShareOpen] = useState(false)
    const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false)

    useEffect(() => {
        (async () => {
            if (!documentId) return

            setIsLoading(true)

            try {
                await Promise.all([
                    documentViewController.load( documentId ),
                    documentACLController.init( documentId, sessionService.currentUserId )
                ])

            } catch (error) {
                if (error?.status === 401 && error?.code === 'AUTHORIZATION_REQUIRED') {
                    addToast({
                        color: 'warning',
                        title: 'Notice',
                        message: `No permissions for Document ID#${documentId}`
                    })
                    if (onDismiss) {
                        onDismiss()
                    } else {
                        navigate('/everdocs/')
                    }
                }

                console.error(error.message)
            }

            setIsLoading(false)
        })()

    }, [documentId, sessionService.currentUserId])

    useEffect(() => {
        departmentsAccessController.init(sessionService.currentUserId)
    }, [])

    const toggleArchive = async () => {
        try {
            documentViewController.setArchive(!documentViewController.document?.isArchived)

        } catch (error: any) {
            console.error(error.message)
        }
    }

    const deleteDocument = async () => {
        const deleteIt = async () => {
            try {
                const prevName = documentViewController.name

                await documentViewController.delete()

                addToast({
                    color: 'success',
                    title: 'Delete successful',
                    message: `"${prevName}" has been permanently deleted`
                })

                if (onDismiss) {
                    onDismiss()
                } else {
                    navigate('/everdocs/')
                }

            } catch (error: any) {
                addToast({
                    color: 'danger',
                    title: 'Error',
                    message: error?.message || error
                })
            }
        }

        const confirmed = await isConfirmed({
            title: 'Delete EverDoc?',
            message: 'Since you have not shared this document with others it can be deleted. Are you sure you want to delete this EverDoc?',
            acceptButton: {
                label: 'Delete',
                color: 'danger'
            }
        })
        if (confirmed) {
            deleteIt()
        }
    }

    const onClone = async () => {

        showAppLoader({ name: 'app-loading', isOpen: true })

        try {
            const { id } = await documentViewController.cloneDocument()

            navigate(`/everdocs/edit/${id}`)
        } catch (error: any) {
            if (error instanceof EverdocMediaUplaodError) {
                addToast({
                    color: 'warning',
                    title: 'Be aware',
                    message: `Document cloned with some media issues`
                })
                // take them to the edit screen
                navigate(`/everdocs/edit/${error?.document?.id}`)

            } else {
                addToast({
                    color: 'danger',
                    title: 'Something went wrong',
                    message: error.message
                })
            }
        } finally {
            showAppLoader({ name: 'app-loading', isOpen: false })
        }
    }

    const exportCSV = async () => {
        try {
            const blob = await documentViewController.downloadCSV(documentId)
            exportDocumentAsCSV(blob, document.name)

        } catch (error: any) {
            console.error(error.message)
        }
    }

    const handleTrackPdfDownload = async () => {
        try {
            await documentViewController.trackPdfDownload(documentId)
        } catch (error) {
            console.error('Error tracking PDF download:', error)
        }
    }

    if (isLoading || !documentViewController.document) {
        return <DocumentPlaceholder />
    }

    const document = documentViewController.document

    const schema = documentViewController.documentSchema
    const canEdit = documentACLController.canEdit
    const canShare = documentACLController.canShare

    const userAccessInDocumentDept = getSpecificDepartmentsAccess(departmentsAccessController.departmentsAccess, document.departmentId)
    const userRoleInDocumentDept = getRole(userAccessInDocumentDept?.roles)

    const canEditDocument = canEdit || userRoleInDocumentDept === 'executive'

    const ownerACL = documentACLController.aclOwner?.[0]

    const shareTooltipText = (!canShare) ?
        'You do not have sharing permissions for this document' :
        (document.isArchived) ? 'You can not share archived documents' : ''

    const editTooltipText = (!canEditDocument) ?
        'You do not have edit permissions for this document' :
        (document.isArchived) ? 'You can not edit archived documents' : ''

    return (
        <Card id="document-full-view-card">
            <CardHeader className="flex-wrap gap-2">
                {/* icon + name + badge move as one unit so the name never wraps away from its icon */}
                <div className="d-flex align-items-center min-width-0 flex-grow-1">
                    <DocumentIcon
                        className="me-2 flex-shrink-0"
                        schema={documentViewController?.documentSchema as APIDataDocumentSchema}
                        width={40}
                        height={44}
                    />
                    <Text tag="h3" bold className="me-2 min-width-0 text-break">
                        {document?.name}
                    </Text>
                    {(document.isArchived) &&
                        <Badge
                            color="gray400"
                            className="me-2 flex-shrink-0">
                            Archived
                        </Badge>
                    }
                </div>
                {/* basis is its content width, so it drops to its own line rather than squeezing
                    the owner name; items wrap within it, and center so buttons never stretch */}
                <CardHeaderActions
                    className="flex-wrap align-items-center row-gap-2 min-width-0"
                    style={{ flex: '1 0 auto', maxWidth: '100%' }}>
                    <DocumentOwnerDisplay
                        type="label"
                        truncate
                        selectedOwner={ownerACL?.principal}
                    />
                    <div className="mx-3">
                        <DocumentMenu
                            attachId={'document-full-view-card'}
                            document={document}
                            userId={sessionService.currentUserId}
                            schema={schema as APIDataDocumentSchema}
                            canEdit={canEditDocument}
                            canShare={false}
                            canClone={userRoleInDocumentDept !== 'guest'}
                            onClone={onClone}
                            onToggleArchive={toggleArchive}
                            onExportAsCSV={() => exportCSV()}
                            onTrackPdfDownload={handleTrackPdfDownload}
                            onOpenVersionHistory={() => setIsVersionHistoryOpen(true)}
                            {...(!documentACLController.hasPreviousShares) && { onDelete: deleteDocument }}
                        />
                    </div>
                    {(canEditDocument && !document.isArchived) ?
                        <Button
                            color="neutral"
                            className="ms-2 text-nowrap"
                            onClick={() => onEdit(document.id)}
                        >
                            <Icon
                                name="edit"
                                className="me-1"
                            />
                            Edit
                        </Button>
                        :
                        <Button color={'gray'}
                            aria-disabled="true"
                            className="ms-2 disabled text-nowrap" data-tooltip-pos="top" tabIndex={0}
                            data-tooltip={editTooltipText}
                        >
                            <Icon name={'lock'} className="me-1" color={'gray600'} />
                            Edit
                        </Button>
                    }
                    {(canShare && !document.isArchived) ?
                        <Button
                            color={'primary'}
                            className="ms-2 text-nowrap"
                            onClick={() => setIsShareOpen(true)}
                        >
                            <Icon
                                name={'share'}
                                className="me-1"
                            />
                            Share
                        </Button>
                        :
                        <Button color={'gray'}
                            aria-disabled="true"
                            className="ms-2 disabled text-nowrap" data-tooltip-pos="top" tabIndex={0}
                            data-tooltip={shareTooltipText}
                        >
                            <Icon name={'lock'} className="me-1" color={'gray600'} />
                            Share
                        </Button>
                    }
                </CardHeaderActions>
            </CardHeader>
            <CardBody>
                <DocumentServiceProvider services={[{
                    id: 'meta', value: { media: documentViewController.document?.media }
                }]}>
                    <WidgetsProvider value={ReadWidgets}>
                        <Document
                            layout="full"
                            schema={schema?.schema}
                            schemaId={schema?.id}
                            documentId={documentId}
                            value={document.data}
                        />
                    </WidgetsProvider>
                </DocumentServiceProvider>
            </CardBody>
            <CardFooter>
                <DocumentFooter
                    document={document}
                    schema={schema}
                />
            </CardFooter>
            <DocumentShareModal
                visible={isShareOpen}
                document={document}
                documentACLController={documentACLController}
                onClose={() => setIsShareOpen(false)}
                backdrop={!nested}
            />
            <DocumentVersionHistoryModal
                visible={isVersionHistoryOpen}
                documentId={documentId}
                schema={schema as APIDataDocumentSchema}
                media={document?.media}
                currentOwner={ownerACL?.principal}
                onClose={() => setIsVersionHistoryOpen(false)}
            />
        </Card>
    )
})

export {
    DocumentFullView
}
